import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { doLogin } from 'store/actionCreators/auth';
import { useSignInForm } from './form';
import { useInfoPopup } from 'hooks';
import {
  getAmplifyAuthErrorMessage,
  mightBeSocialAccountError,
  CognitoError,
} from '../utils';

export const useSignIn = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { handleSubmit, setValue, ...form } = useSignInForm();
  const { showInfoPopup } = useInfoPopup();

  const onFailure = (error: CognitoError) => {
    const title = getAmplifyAuthErrorMessage(t, error);

    // Only show social login warning if we can reasonably determine
    // the account is linked to social providers
    //
    // IMPORTANT: UserNotFoundException alone is NOT a reliable indicator
    // because it can occur for:
    // - Pre-registered accounts not yet fully set up in Cognito
    // - Non-existent accounts
    // - Deleted accounts
    //
    // We only show social warning if:
    // 1. Error message explicitly mentions social providers, OR
    // 2. We have other indicators (e.g., NotAuthorizedException with social indicators)
    const isLikelySocialAccount = mightBeSocialAccountError(error);

    if (isLikelySocialAccount) {
      showInfoPopup({
        type: 'error',
        title: t('auth.socialLoginDetected.title'),
        message: t('auth.socialLoginDetected.message'),
      });
    } else {
      // For all other cases, show the standard error message
      // This includes UserNotFoundException which should NOT trigger social warning
      showInfoPopup({
        type: 'error',
        title,
      });
    }
  };

  const onSubmit = handleSubmit((formData) => {
    dispatch(
      doLogin({
        email: formData.email.toLowerCase(),
        password: formData.password,
        onFailure,
      }),
    );
  });

  return {
    onSubmit,
    setValue,
    ...form,
  };
};
